import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchPtoDeletedShifts } from "../components/staffing/downloadScheduleUtils";
import { useAnalytics } from "../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useResolvedActualDay, useWeekViewContext } from "../contexts/WeekView";
import { buildAiPrintoutData } from "../downloads/aiPrintout/buildAiPrintoutData";
import type { AiPrintoutData } from "../downloads/aiPrintout/contract";
import { isCaliforniaDepartment } from "../utils/californiaDepartment";
import { useAiPrintoutLabels } from "./useAiPrintoutLabels";
import { useAiPrintoutViewerInputs } from "./useAiPrintoutViewerInputs";
import { useDepartmentCallInsForDay } from "./useCallIns";
import { useCallOffSourceLabel } from "./useCallOffSourceLabel";
import { useCensusPointInTimesLoader } from "./useCensusPointInTimes";
import { useLastNameFirst } from "./useNameFormat";
import { useFeatureFlag } from "./useOpenFeatureFlag";

const AI_PRINTOUT_PTO_QUERY_KEY = "aiPrintoutPtoDeletedShifts";
const AI_PRINTOUT_CENSUS_READINGS_QUERY_KEY = "aiPrintoutCensusReadings";

export type AiPrintoutDataState = {
	/** Null until every input has loaded, and when one failed. */
	data: AiPrintoutData | null;
	isLoading: boolean;
	/** Time off or call outs failed to load, or the viewed day isn't in the loaded week. */
	isError: boolean;
};

/**
 * The day's CDPH census readings, as the Detailed printout loads them (`useCensusPointInTimesLoader`): only for a
 * California department, and a failure is tracked and leaves the readings out rather than failing the printout.
 */
const useCensusReadings = (date: DateTime) => {
	const { chosenDepartment, isAnyDemo } = useHospitalData();
	const { trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const reportFailure = useCallback(
		(error: unknown) =>
			trackError("census_point_in_times_failed", {
				department_id: departmentId,
				date: date.toISODate(),
				error_message: error instanceof Error ? error.message : String(error),
			}),
		[departmentId, date, trackError],
	);
	const loadReadings = useCensusPointInTimesLoader(departmentId, isCaliforniaDepartment(chosenDepartment.timezone, isAnyDemo), reportFailure);
	return useQuery({
		queryKey: [AI_PRINTOUT_CENSUS_READINGS_QUERY_KEY, departmentId, date.toISODate()],
		// The loader answers undefined for "no readings" (it never rejects); a query can't hold undefined, so null.
		queryFn: async () => (await loadReadings(date)) ?? null,
	});
};

/**
 * The data a custom printout renders for the viewed day — the same sources the Detailed printout reads
 * (`usePrintout`), minus every Day view filter, with the actual side as the Day view's actual mode resolves it.
 * Time off and call outs are always fetched, since any template may print them.
 */
export const useAiPrintoutData = (date: DateTime, showActual: boolean): AiPrintoutDataState => {
	const { i18n } = useTranslation();
	const { hospitalData, chosenDepartment, getWeekIndexByDate } = useHospitalData();
	const { weekViewData } = useWeekViewContext();
	const actualDay = useResolvedActualDay(date);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const labels = useAiPrintoutLabels();
	const callOffSourceLabel = useCallOffSourceLabel();
	const callOuts = useDepartmentCallInsForDay({ date });
	const censusReadings = useCensusReadings(date);
	const isoDate = date.toFormat("yyyy-MM-dd");
	const viewerInputs = useAiPrintoutViewerInputs(isoDate);
	const departmentId = chosenDepartment.department_id;
	const ptoQuery = useQuery({
		queryKey: [AI_PRINTOUT_PTO_QUERY_KEY, departmentId, isoDate, lastNameFirst],
		queryFn: () => fetchPtoDeletedShifts(departmentId, isoDate, isoDate, hospitalData.nurses, lastNameFirst),
	});
	const dayItem = weekViewData.at(date.weekdayISO);
	const isLoading = ptoQuery.isLoading || callOuts.isLoading || censusReadings.isLoading;

	const data = useMemo(() => {
		// Failed call outs would read as none, printing a sheet without them: no data instead.
		if (dayItem === undefined || ptoQuery.data === undefined || censusReadings.data === undefined || isLoading || callOuts.isError) return null;
		return buildAiPrintoutData({
			...viewerInputs,
			date,
			isActual: showActual,
			scheduledShifts: dayItem.shifts,
			actualDay,
			dayStats: dayItem.dayStats,
			dayStatsActual: dayItem.dayStatsActual,
			ptoShifts: ptoQuery.data,
			callOuts: callOuts.callIns,
			hospitalData,
			weekIndex: getWeekIndexByDate(date),
			department: chosenDepartment,
			jobManagementEnabled,
			lastNameFirst,
			censusReadings: censusReadings.data,
			locale: i18n.language,
			dir: i18n.dir(),
			labels,
			callOffSourceLabel,
		});
	}, [
		viewerInputs,
		dayItem,
		actualDay,
		ptoQuery.data,
		censusReadings.data,
		isLoading,
		date,
		showActual,
		callOuts.callIns,
		callOuts.isError,
		hospitalData,
		getWeekIndexByDate,
		chosenDepartment,
		jobManagementEnabled,
		lastNameFirst,
		i18n,
		labels,
		callOffSourceLabel,
	]);

	return { data, isLoading, isError: ptoQuery.isError || callOuts.isError || (!isLoading && dayItem === undefined) };
};
